import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import helpers from 'src/helpers';
import { MESSAGES, STORAGE } from 'src/constants';

const { VCARD_FILENAME } = MESSAGES;

/**
 * CON-1625 — one renderer for "what is attached to this template".
 *
 * Extracted from TBodyResultsMessage, which had the only copy. It is now
 * needed on the Message Template apply modal, the Follow-Up apply modal, and
 * the Share Center preview, and a template with a vCard or a PDF rendering
 * as blank space in three of those four places is what prompted the split.
 *
 * The key difference from the list usage: a SHARE preview shows a blob owned
 * by the AUTHOR, so ownerSalespersonId and container come from the verify
 * payload. Passing the viewer's id would build a URL into the wrong
 * container and the image would silently 404.
 */
const isContactCardAttachment = (fileName) => fileName === VCARD_FILENAME;

const stripDatetimePrefix = (fileName) => {
  if (!fileName) return '';
  return fileName.replace(/^\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2,3}-/, '');
};

const getFileExtension = (fileName) => (
  !fileName ? '' : fileName.toLowerCase().split('.').pop()
);

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav', 'ogg', 'aac', 'wma', 'flac'];

const isImageFile = (fileName) => IMAGE_EXTENSIONS.includes(getFileExtension(fileName));
const isAudioFile = (fileName) => AUDIO_EXTENSIONS.includes(getFileExtension(fileName));

function AttachmentPreview({
  attachment = null,
  attachmentContainer = null,
  ownerSalespersonId = null,
  size = 96,
}) {
  const { azAccountName, databaseBeingUsed } = useSelector((state) => state.account);

  if (!attachment) return null;

  // A contact card is the RECIPIENT's own card at send time, never the
  // author's file, so it is named rather than previewed.
  if (isContactCardAttachment(attachment)) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        background: '#F0F7FF',
        border: '1px solid #1976D2',
        borderRadius: '4px',
        padding: '6px 10px',
        maxWidth: 180,
        flexShrink: 0,
      }}
      >
        <ContactPageIcon sx={{ color: '#1976D2', fontSize: 24 }} />
        <Typography variant="body2" fontWeight={500} noWrap>
          Your Contact Card
        </Typography>
      </Box>
    );
  }

  if (isImageFile(attachment)) {
    return (
      <Box
        component="img"
        src={helpers.azure.getImageFileURL(
          attachment,
          ownerSalespersonId,
          azAccountName,
          STORAGE.sas,
          databaseBeingUsed,
          attachmentContainer,
          true,
        )}
        alt={attachment}
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          objectFit: 'contain',
          border: '1px solid #CCCCCC',
          borderRadius: '4px',
          padding: '4px',
          backgroundColor: '#FFFFFF',
        }}
      />
    );
  }

  // PDF, audio, or anything else: named with a type-appropriate icon rather
  // than rendered, which is the whole point of this component existing.
  const ext = getFileExtension(attachment);
  const displayName = stripDatetimePrefix(attachment);
  let FileIcon = InsertDriveFileIcon;
  let iconColor = '#757575';
  if (ext === 'pdf') {
    FileIcon = PictureAsPdfIcon;
    iconColor = '#d32f2f';
  } else if (isAudioFile(attachment)) {
    FileIcon = AudioFileIcon;
    iconColor = '#7B1FA2';
  }

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      background: '#F5F5F5',
      border: '1px solid #E0E0E0',
      borderRadius: '4px',
      padding: '6px 10px',
      maxWidth: 180,
      flexShrink: 0,
    }}
    >
      <FileIcon sx={{ color: iconColor, fontSize: 24 }} />
      <Typography
        variant="body2"
        noWrap
        title={displayName}
        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {displayName}
      </Typography>
    </Box>
  );
}

AttachmentPreview.propTypes = {
  attachment: PropTypes.string,
  attachmentContainer: PropTypes.string,
  // The blob's OWNER. On a share preview this is the author, not the viewer.
  ownerSalespersonId: PropTypes.number,
  size: PropTypes.number,
};

export default AttachmentPreview;
